/**
 * Approval-status styling primitives.
 *
 * Single source of truth for how a service's approval state (requested /
 * approved / denied / auto-approved) is expressed anywhere in the app —
 * status pills, tinted rows, accent bars. Every color here is DERIVED from
 * the existing semantic status palette (`colors.status.*`); no new hues are
 * introduced, tints are the same tokens at low alpha.
 *
 * Use `ApprovalStatusPill` (components/feedback) for the pill treatment and
 * `approvalSurfaceStyle` for row/card surfaces so the approved / pending /
 * denied language stays identical on every screen.
 */
import { colors } from './resolvedColors';
import { withAlpha } from './colorUtils';

export type ApprovalStatusKey = 'requested' | 'approving' | 'approved' | 'denied' | 'auto_approved';

export interface ApprovalStatusStyle {
  /** Text / icon / accent-bar color — an existing `colors.status` token */
  accent: string;
  /** Low-alpha surface fill for rows and cards */
  tint: string;
  /** Stronger fill for pills and badges */
  tintStrong: string;
  /** Translucent border for pill outlines */
  border: string;
  /** Human-readable label */
  label: string;
}

const make = (accent: string, label: string): ApprovalStatusStyle => ({
  accent,
  tint: withAlpha(accent, 0.08),
  tintStrong: withAlpha(accent, 0.16),
  border: withAlpha(accent, 0.4),
  label,
});

export const approvalStatusStyles: Record<ApprovalStatusKey, ApprovalStatusStyle> = {
  requested: make(colors.status.warning, 'Pending Approval'),
  // Display-only: a service that no one has decided yet, shown DURING an
  // approval review where the default is to approve. No backend status maps
  // here — a surface opts in by passing 'approving' explicitly. It reads
  // green because that is what happens when the reviewer hits APPROVE, but it
  // stays distinct from 'approved' because nothing is recorded yet.
  approving: make(colors.status.success, 'Approving'),
  approved: make(colors.status.success, 'Approved'),
  denied: make(colors.status.error, 'Denied'),
  auto_approved: make(colors.status.info, 'Auto-Approved'),
};

/**
 * Map raw backend / legacy strings onto the canonical keys.
 * Accepts the aliases that already circulate in the app ('pending',
 * 'declined', 'rejected', ...) so every surface normalizes the same way.
 */
export const normalizeApprovalStatus = (status: string | undefined | null): ApprovalStatusKey | undefined => {
  if (!status) return undefined;
  switch (status.toLowerCase()) {
    case 'approved':
      return 'approved';
    case 'approving':
      return 'approving';
    case 'requested':
    case 'pending':
    case 'pending_approval':
    case 'awaiting_approval':
      return 'requested';
    case 'denied':
    case 'declined':
    case 'rejected':
      return 'denied';
    case 'auto_approved':
    case 'auto-approved':
      return 'auto_approved';
    default:
      return undefined;
  }
};

/**
 * Row/card surface treatment: low-alpha status fill plus a 3px accent bar.
 * The bar is drawn as an inset shadow (not a border) so applying it never
 * shifts layout relative to untreated rows.
 */
export const approvalSurfaceStyle = (status: ApprovalStatusKey): { backgroundColor: string; boxShadow: string } => {
  const s = approvalStatusStyles[status];
  return {
    backgroundColor: s.tint,
    boxShadow: `inset 3px 0 0 0 ${s.accent}`,
  };
};
